import { z } from 'zod';

import { zodTimeStamp } from '@/utils/zodUtils';

export enum HoldStatus {
  PENDING = 'pending',
  HELD = 'held',
  ALLOCATED = 'allocated',
}

export const ReservedSpaceHoldSchema = z.object({
  id: z.string().uuid(),
  group_subagreement_id: z.string().uuid(),
  group_agreement_id: z.string().uuid().nullable().optional(),
  group_seat_id: z.string().uuid().nullable().optional(),
  status: z.nativeEnum(HoldStatus),
  organization_name: z.string().optional(),
  tier_name: z.string().optional(),
  expires_at: zodTimeStamp.nullable().optional(),
});

export const ReservedSpaceSchema = z.object({
  id: z.string().uuid(),
  location_id: z.string(),
  space_identifier: z.string(),
  created_at: zodTimeStamp,
  updated_at: zodTimeStamp,
  active_hold: ReservedSpaceHoldSchema.nullable().optional(),
});

export const ReservedSpaceListSchema = z.array(ReservedSpaceSchema);

const BreakdownItemSchema = z.object({
  reserved: z.number(),
  unreserved: z.number(),
});

const CapacityBreakdownSchema = z.object({
  individual_agreements: BreakdownItemSchema,
  group_subagreements: BreakdownItemSchema,
});

export enum CapacityStatus {
  OK = 'ok',
  OVER_COMMITTED = 'over_committed',
}

export enum CommitmentHoldStatus {
  PENDING = 'pending',
  ACTIVE = 'active',
  RELEASED = 'released',
}

// The floor fields are optional so the portal keeps parsing the summary from
// an API that predates them.
export const CapacityStatsSchema = z.object({
  capacity_total: z.number(),
  reserved_total: z.number(),
  unreserved_total: z.number(),
  reserved_used: z.number(),
  unreserved_used: z.number(),
  reserved_available: z.number(),
  unreserved_available: z.number(),
  breakdown: CapacityBreakdownSchema,
  physical_known: z.boolean().optional(),
  oversell_percent: z.number().optional(),
  unreserved_sellable: z.number().optional(),
  pool_committed: z.number().optional(),
  per_permit_committed: z.number().optional(),
  over_committed_by: z.number().optional(),
  reserved_over_committed_by: z.number().optional(),
  status: z.nativeEnum(CapacityStatus).optional(),
});

export const PoolCommitmentSchema = z.object({
  tier_id: z.string().uuid(),
  group_agreement_id: z.string().uuid(),
  organization_name: z.string(),
  tier_name: z.string(),
  committed: z.number().int(),
  hold_status: z.nativeEnum(CommitmentHoldStatus),
  expires_at: zodTimeStamp.nullish(),
});

export const PerPermitCommitmentSchema = z.object({
  agreement_id: z.string().uuid(),
  group_agreement_id: z.string().uuid().nullish(),
  quantity: z.number().int(),
  hold_status: z.nativeEnum(CommitmentHoldStatus),
  expires_at: zodTimeStamp.nullish(),
});

export const ReservedCommitmentSchema = z.object({
  tier_id: z.string().uuid().nullish(),
  agreement_id: z.string().uuid().nullish(),
  group_agreement_id: z.string().uuid().nullish(),
  organization_name: z.string().nullish(),
  tier_name: z.string().nullish(),
  spaces: z.number().int(),
  hold_status: z.nativeEnum(CommitmentHoldStatus),
  expires_at: zodTimeStamp.nullish(),
});

export const CapacityCommitmentsSchema = z.object({
  location_id: z.string(),
  physical_known: z.boolean(),
  physical_total: z.number().int(),
  reserved_spaces: z.number().int(),
  unreserved_physical: z.number().int(),
  oversell_percent: z.number().int(),
  reserved_used: z.number().int(),
  pools: z.array(PoolCommitmentSchema),
  per_permit: z.array(PerPermitCommitmentSchema),
  reserved: z.array(ReservedCommitmentSchema),
  pool_committed: z.number().int(),
  per_permit_committed: z.number().int(),
  unreserved_sellable: z.number().int(),
  unreserved_available: z.number().int(),
  reserved_available: z.number().int(),
  over_committed_by: z.number().int(),
  reserved_over_committed_by: z.number().int(),
  status: z.nativeEnum(CapacityStatus),
});

export const CreateReservedSpacesRequestSchema = z.object({
  space_identifiers: z.array(z.string().min(1)).min(1),
});

export type ReservedSpaceHoldType = z.infer<typeof ReservedSpaceHoldSchema>;
export type ReservedSpaceType = z.infer<typeof ReservedSpaceSchema>;
export type ReservedSpaceListType = z.infer<typeof ReservedSpaceListSchema>;
export type CapacityStatsType = z.infer<typeof CapacityStatsSchema>;
export type PoolCommitmentType = z.infer<typeof PoolCommitmentSchema>;
export type PerPermitCommitmentType = z.infer<typeof PerPermitCommitmentSchema>;
export type ReservedCommitmentType = z.infer<typeof ReservedCommitmentSchema>;
export type CapacityCommitmentsType = z.infer<typeof CapacityCommitmentsSchema>;
export type CreateReservedSpacesRequestType = z.infer<
  typeof CreateReservedSpacesRequestSchema
>;
